import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';

import type { DashboardComment } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import type { ApiComment } from 'src/components/dashboard/DashboardContext/CommentsContext';
import type { SentimentFilterValues } from 'src/components/dashboard/hooks/useTagsQuery';

import useDashboardFilters from './useDashboardFilters';

const mapApiComment = (apiComment: ApiComment): DashboardComment => ({
  sentimentOverall: apiComment.sentiment_overall,
  text: apiComment.text,
  translatedText: apiComment.translated_text,
  topics: apiComment.topics,
});

interface ApiCommentsPagination {
  total_pages: number;
}

interface ApiCommentsPayload {
  data: Array<ApiComment>;
  pagination: ApiCommentsPagination;
}

const fetchComments = async (
  endpoint: string,
  language: string,
  filters: Record<string, string[]>,
  question: string | null,
  commentIds?: string[],
  source?: string,
  page?: number,
  textFilter?: string,
  sentimentFilter?: SentimentFilterValues | null,
): Promise<[DashboardComment[], number]> =>
  HttpService.post(endpoint, {
    filters,
    comment_ids: commentIds,
    source,
    language,
    page,
    question: question || '',
    text_filter: textFilter || '',
    sentiment_filter: sentimentFilter,
  })
    .then(({ data }: AxiosResponse<ApiCommentsPayload>) => {
      if (!data?.data) {
        return Promise.resolve([[], 1] as [DashboardComment[], number]);
      }
      return Promise.resolve([
        data.data.map(mapApiComment),
        data.pagination.total_pages,
      ] as [DashboardComment[], number]);
    })
    .catch((error: AxiosError) => Promise.reject(error.response));

const useCommentsQuery = (
  source: 'topics' | 'token' | 'hrqs',
  commentIds: string[],
  question: string | null,
  currentPage?: number,
  textFilter?: string,
  sentimentFilter?: SentimentFilterValues | null,
  dashboardId?: number,
) => {
  const { flatFilters } = useDashboardFilters();
  const { i18n } = useTranslation();

  const endpoint = useMemo(
    () => generatePath(apiPaths.DASHBOARD.COMMENTS, { id: dashboardId }),
    [dashboardId],
  );

  return useQuery<[DashboardComment[], number], AxiosError>({
    enabled: !!commentIds.length && source !== 'hrqs' && !!dashboardId,
    queryKey: [
      endpoint,
      i18n.language,
      flatFilters,
      commentIds,
      source,
      currentPage,
      question,
      textFilter,
      sentimentFilter,
    ],
    queryFn: () =>
      fetchComments(
        endpoint,
        i18n.language,
        flatFilters,
        question,
        commentIds,
        source,
        currentPage,
        textFilter,
        sentimentFilter,
      ),
  });
};

export default useCommentsQuery;
